import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, track, formatApiError } from "@/lib/api";
import { fieldCls } from "@/components/site/LeadForms";

const ALL_COUNTRIES = ["United Kingdom", "USA", "Canada", "Australia", "Germany", "Ireland", "New Zealand", "France", "Italy", "Romania", "Netherlands", "Switzerland", "Spain", "UAE", "Singapore", "Malaysia", "Mauritius", "Not sure yet"];
const TEST_STATUS = ["Not taken yet", "IELTS", "PTE", "TOEFL", "Duolingo", "GRE", "GMAT", "Planning to take"];

export default function ProfileAssessment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first: "", last: "", email: "", phone: "", qualification: "", graduation_year: "",
    destination: "Romania", course: "", study_level: "Bachelor's", intake: "", budget: "",
    test_status: "", work_experience: "", message: "", consent: false,
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please agree to be contacted regarding your enquiry.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        type: "profile-assessment",
        name: `${form.first} ${form.last}`.trim(),
        email: form.email || null,
        phone: form.phone,
        qualification: form.qualification,
        graduation_year: form.graduation_year,
        destination: form.destination,
        course: form.course,
        study_level: form.study_level,
        intake: form.intake,
        budget: form.budget,
        test_status: form.test_status,
        work_experience: form.work_experience,
        message: form.message,
        consent: form.consent,
      });
      track("lead_submit", { type: "profile-assessment" });
      track("assessment_complete");
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="profile-assessment-card" className="bg-white rounded-3xl border border-[#E6EBF1] shadow-2xl shadow-slate-900/8 p-7 sm:p-10">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-extrabold text-[#172033] tracking-tight">NOT SURE WHERE TO STUDY?</h3>
        <p className="text-sm text-[#5F6B7A] mt-2 max-w-lg mx-auto">
          Tell us about your academic background and goals. Explore suitable countries, courses and universities.
        </p>
      </div>
      <form data-testid="profile-assessment-form" onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <input data-testid="pa-first-name" required placeholder="First Name" className={fieldCls} value={form.first} onChange={set("first")} />
        <input data-testid="pa-last-name" required placeholder="Last Name" className={fieldCls} value={form.last} onChange={set("last")} />
        <input data-testid="pa-phone" required placeholder="Phone / WhatsApp" className={fieldCls} value={form.phone} onChange={set("phone")} />
        <input data-testid="pa-email" type="email" placeholder="Email Address" className={fieldCls} value={form.email} onChange={set("email")} />
        <select data-testid="pa-education" required className={fieldCls} value={form.qualification} onChange={set("qualification")}>
          <option value="" disabled>Current Education</option>
          {["Class 12", "Diploma", "Bachelor's (pursuing)", "Bachelor's (completed)", "Master's", "Other"].map((q) => <option key={q}>{q}</option>)}
        </select>
        <input data-testid="pa-grad-year" placeholder="Graduation Year (e.g. 2025)" className={fieldCls} value={form.graduation_year} onChange={set("graduation_year")} />
        <select data-testid="pa-country" className={fieldCls} value={form.destination} onChange={set("destination")}>
          {ALL_COUNTRIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input data-testid="pa-course" placeholder="Preferred Course" className={fieldCls} value={form.course} onChange={set("course")} />
        <select data-testid="pa-level" className={fieldCls} value={form.study_level} onChange={set("study_level")}>
          <option>Bachelor's</option>
          <option>Master's</option>
          <option>MBA</option>
          <option>PhD</option>
          <option>Diploma / Certificate</option>
        </select>
        <select data-testid="pa-intake" className={fieldCls} value={form.intake} onChange={set("intake")}>
          <option value="" disabled>Preferred Intake</option>
          {["Autumn 2026", "Spring 2027", "Autumn 2027", "2028 or later", "Not sure yet"].map((i) => <option key={i}>{i}</option>)}
        </select>
        <select data-testid="pa-budget" className={fieldCls} value={form.budget} onChange={set("budget")}>
          <option value="" disabled>Yearly Budget</option>
          {["Under €4,000", "€4,000 - €8,000", "€8,000 - €15,000", "€15,000 - €30,000", "Above €30,000", "Need guidance"].map((b) => <option key={b}>{b}</option>)}
        </select>
        <select data-testid="pa-test-status" className={fieldCls} value={form.test_status} onChange={set("test_status")}>
          <option value="" disabled>IELTS / PTE / TOEFL Status</option>
          {TEST_STATUS.map((t) => <option key={t}>{t}</option>)}
        </select>
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="flex items-start gap-3 text-xs text-[#5F6B7A] cursor-pointer">
            <input data-testid="pa-consent" type="checkbox" checked={form.consent} onChange={set("consent")}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#1E40AF] focus:ring-[#1E40AF]/30" />
            I agree to be contacted regarding my education enquiry.
          </label>
        </div>
        <div className="sm:col-span-2 lg:col-span-3 text-center">
          <button data-testid="pa-submit-btn" disabled={loading}
            className="px-10 py-4 rounded-full text-sm font-bold text-white bg-[#1E40AF] hover:bg-[#1D4ED8] shadow-xl shadow-blue-900/20 active:scale-[0.98] transition-all inline-flex items-center gap-2 disabled:opacity-60">
            {loading ? "ASSESSING..." : "ASSESS MY PROFILE"} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 mt-3">
            <Lock className="w-3 h-3" /> Free &amp; confidential. No spam, ever.
          </p>
        </div>
      </form>
    </div>
  );
}
